import type { ProjectPermission } from './api/projectPermissions';

/**
 * 프로젝트 내비게이션 라우트의 단일 소스.
 *
 * 사이드바 메뉴(`AppProviders`)와 프로젝트 랜딩 결정(`workspaceRouting`)이 같은 배열을 읽는다.
 * 두 벌로 나뉘면 메뉴 순서와 랜딩 목적지가 조용히 어긋난다.
 *
 * **배열 순서 = 랜딩 우선순위**다. 메뉴 표시 순서를 바꾸면 랜딩 목적지도 함께 바뀐다.
 * 라벨·아이콘·그룹 구성은 여기 두지 않는다(i18n 동적 키 사각지대를 만들지 않기 위해).
 * 서버의 API 라우트 매트릭스(`api/src/constants/projectRoutePermissions.ts`)와는 별개 축이다.
 */
export const PROJECT_NAV_ROUTES = [
  { permission: 'dashboard.read', path: '/dashboard' },
  { permission: 'plans.read', path: '/plans' },
  { permission: 'runners.read', path: '/daemon-triggers' },
  { permission: 'coActions.read', path: '/co-actions' },
  { permission: 'reports.read', path: '/completion-reports' },
  { permission: 'codeReviews.read', path: '/code-reviews' },
  { permission: 'postMortems.read', path: '/post-mortems' },
  { permission: 'conventions.read', path: '/conventions' },
  { permission: 'skills.read', path: '/skills' },
  { permission: 'documents.read', path: '/documents' },
  { permission: 'agents.read', path: '/agent-configs' },
] as const satisfies readonly { permission: ProjectPermission; path: string }[];

export type ProjectNavRoute = (typeof PROJECT_NAV_ROUTES)[number];
export type ProjectNavPermission = ProjectNavRoute['permission'];

/**
 * 접근 가능한 라우트가 하나도 없을 때 쓰는 목적지.
 *
 * 이 경우에도 어딘가로는 보내야 하는 호출부(프로젝트 선택 등)만 사용한다. 여기로 보내면
 * `ProjectFeatureGate`가 접근 거부 UI를 보여주며 멈춘다 — 게이트는 리다이렉트하지 않으므로 루프가 없다.
 */
export const PROJECT_LANDING_FALLBACK_PATH = '/dashboard';

export const getProjectNavPath = (permission: ProjectNavPermission): string => {
  const route = PROJECT_NAV_ROUTES.find((item) => item.permission === permission);
  // 타입상 도달 불가. 배열에서 항목을 지우면 컴파일이 먼저 막는다.
  return route ? route.path : PROJECT_LANDING_FALLBACK_PATH;
};

/** pathname이 프로젝트 내비 라우트면 그 항목을, 아니면(상세 화면 등) undefined를 돌려준다. */
export const findProjectNavRoute = (pathname: string): ProjectNavRoute | undefined => {
  return PROJECT_NAV_ROUTES.find((route) => route.path === pathname);
};

/**
 * 현재 permission으로 접근 가능한 첫 프로젝트 화면. 하나도 없으면 `null`.
 *
 * `null`을 `/dashboard`로 바꾸지 않는다 — 호출부가 "리다이렉트하지 않는다"와
 * "거부 화면을 보여준다"를 스스로 고를 수 있어야 한다.
 */
export const resolveProjectLandingPath = (can: (permission: ProjectPermission) => boolean): string | null => {
  return PROJECT_NAV_ROUTES.find((route) => can(route.permission))?.path ?? null;
};
